import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, RefreshCw } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import XeroPayrollPanel from '../../components/payroll/XeroPayrollPanel';
import PurchaseOrderXeroSettings from '../../components/purchaseOrders/PurchaseOrderXeroSettings';
import RemittanceXeroSettings from '../../components/nursery/accounts/RemittanceXeroSettings';
import nurseryAccountsService from '../../services/nurseryAccounts';
import xeroConnectionService, { type XeroConnectionStatus } from '../../services/xeroConnection';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useAuth } from '../../context/AuthContext';
import { useOAuthReturn, isOAuthReturn } from '../../hooks/useOAuthReturn';
import { useOrgTimezone, formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { Dialog, SeatRequired } from '../../components/nursery/accounts/AccountBits';
import type {
  XeroAccount,
  XeroAccountingMapping,
  XeroAccountingStatus,
  XeroFailures,
  XeroTaxRate,
} from '../../types/nurseryAccounts';

/**
 * /accounts/xero — the organisation's own Xero, connected once.
 *
 * A company's payroll and its books are the same Xero organisation, so there
 * is one sign-in here, used by both. Nothing on this page chooses an
 * organisation, of ours or of Xero's: the connection is made for the
 * organisation the person is in, to the one Xero organisation they select on
 * Xero's own screen. A sign-in that selected more than one connects nothing. Below
 * them sit what each does with the connection: Invoices (the accounts they go
 * to, what is waiting, what failed), Purchase orders (sent so the supplier's
 * bill can be made from them in Xero) and Payroll (earnings rates, matching
 * employees, leave). Invoices and Payroll show only where the organisation has
 * the module they serve; purchase orders belong to no module and always show.
 *
 * Xero is the ledger. Invoices and credit notes are sent to it and payments
 * are read back; a payment is never written to Xero from here.
 *
 * Any accounts seat reads the page. Connecting, choosing the accounts,
 * retrying, sending older invoices and everything in Payroll need
 * the Accounts Admin seat, and so do the lists of Xero accounts and tax
 * rates, which the server only gives an admin.
 */

const RETURN_ERRORS: Record<string, string> = {
  access_denied: 'Xero was not connected, because the Xero sign-in was cancelled before it finished. Nothing has changed. If Xero showed your organisation already selected and greyed out, that is normal: press Continue there rather than Cancel.',
  invalid_state: 'Xero was not connected. The sign-in took too long, or was started from somewhere else. Please connect again from this page.',
  missing_params: 'Xero was not connected. Xero did not send back what was needed. Please connect again from this page.',
  connection_failed: 'Xero was not connected. Something went wrong while talking to Xero. Please try again in a few minutes.',
};

type MappingKey = keyof XeroAccountingMapping;

const ACCOUNT_FIELDS: { key: Exclude<MappingKey, 'gst_tax_type'>; label: string; required: boolean; help: string }[] = [
  { key: 'sales_account_code', label: 'Sales account', required: true, help: 'Trees on every invoice go here.' },
  { key: 'delivery_account_code', label: 'Delivery account', required: false, help: 'Delivery charges go here. Left blank, they go to the sales account.' },
  { key: 'rebate_account_code', label: 'Rebate account', required: true, help: 'Rebates and marketing parts are credited here unless a part names its own account.' },
  { key: 'credit_account_code', label: 'Other credits', required: false, help: 'Credit notes for short deliveries, damage and price corrections. Left blank, they go to the sales account.' },
];

/**
 * Where payments taken at the counter land in Xero (2026-10-07). Optional:
 * until one is chosen, those payments stay here and the invoice reads unpaid
 * in Xero. Only an account Xero takes payments into is offered.
 */
const PAYMENT_FIELDS: { key: 'card_clearing_account_code' | 'cash_account_code'; label: string; help: string }[] = [
  { key: 'card_clearing_account_code', label: 'Card payments at the counter',
    help: 'Cards taken at the counter are recorded here against their invoice. Usually a Stripe clearing account, which your Stripe payouts are then matched to.' },
  { key: 'cash_account_code', label: 'Cash at the counter',
    help: 'Cash taken at the counter is recorded here against its invoice: a cash-on-hand or undeposited funds account.' },
];

/** Shown while the organisation does no invoicing: its own status is never asked for. */
const NO_INVOICING: XeroAccountingStatus = {
  enabled_here: true,
  connected: false,
  tenant_selected: false,
  tenant_id: null,
  tenant_name: null,
  xero_user_name: null,
  connected_at: null,
  mapping: { sales_account_code: null, delivery_account_code: null, rebate_account_code: null, credit_account_code: null, gst_tax_type: 'OUTPUT' },
  mapping_missing: [],
  last_sync_at: null,
  last_sync_error: null,
  queue: { invoices_pending: 0, invoices_failed: 0, credit_notes_pending: 0, credit_notes_failed: 0 },
};

const EMPTY_MAPPING: XeroAccountingMapping = {
  sales_account_code: null,
  delivery_account_code: null,
  rebate_account_code: null,
  credit_account_code: null,
  gst_tax_type: 'OUTPUT',
};

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';

/** "a sales account", "a sales account and a rebate account", "a, b and c". */
const joinWithAnd = (items: string[]): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <h2 className="text-base font-semibold text-neutral-900 break-words">{title}</h2>
      {children}
    </section>
  );
}

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <div className="space-y-4 pt-2">
      <div>
        <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{title}</h2>
        <p className="text-sm text-neutral-500 mt-1 break-words">{note}</p>
      </div>
      {children}
    </div>
  );
}

function XeroSettings() {
  const tz = useOrgTimezone();
  const { user } = useAuth();
  const orgName = user?.organisation_name || 'this organisation';
  const [params, setParams] = useSearchParams();
  const { hasAccess: canRead, loading: licenseLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: isAdmin, loading: adminLoading } = useModuleLicense('accounts', 'admin');
  // Which halves of the connection this organisation has a use for. Invoices
  // are the nursery's; payroll goes with timesheets.
  const { orgHasModule: sells, loading: nurseryLoading } = useModuleLicense('nursery');
  const { orgHasModule: paysStaff, loading: timesheetLoading } = useModuleLicense('timesheet');
  const modulesLoading = nurseryLoading || timesheetLoading;

  // What Xero sent the browser back with. Read once, then cleared from the URL.
  // A return carrying a code is not an outcome yet: it is completed below.
  const [returned, setReturned] = useState<{ connected: boolean; error: string | null; message?: string }>(() => (
    isOAuthReturn(params)
      ? { connected: false, error: null }
      : { connected: params.get('connected') === 'true', error: params.get('error') }
  ));

  // The connection itself, and what invoicing does with it.
  const [conn, setConn] = useState<XeroConnectionStatus | null>(null);
  const [status, setStatus] = useState<XeroAccountingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<XeroAccount[]>([]);
  const [taxRates, setTaxRates] = useState<XeroTaxRate[]>([]);
  const [listsError, setListsError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<XeroAccountingMapping>(EMPTY_MAPPING);
  const [failures, setFailures] = useState<XeroFailures>({ invoices: [], credit_notes: [] });

  const [busy, setBusy] = useState<string | null>(null);
  const [retried, setRetried] = useState<Record<string, boolean>>({});
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [backfillFrom, setBackfillFrom] = useState('');
  const [backfillResult, setBackfillResult] = useState<string | null>(null);

  useEffect(() => {
    if (isOAuthReturn(params)) return;
    if (params.has('connected') || params.has('error')) setParams({}, { replace: true });
  }, [params, setParams]);

  /** Everything that hangs off invoicing's status: lists for an admin, failures for anyone. */
  const loadRest = useCallback(async (s: XeroAccountingStatus, admin: boolean) => {
    setMapping({ ...EMPTY_MAPPING, ...s.mapping, gst_tax_type: s.mapping.gst_tax_type || 'OUTPUT' });
    setListsError(null);
    if (!s.connected) {
      setFailures({ invoices: [], credit_notes: [] });
      return;
    }
    try {
      const f = await nurseryAccountsService.getXeroFailures();
      if (f.success && f.data) setFailures({ invoices: f.data.invoices || [], credit_notes: f.data.credit_notes || [] });
    } catch {
      // The counts above still say how many were not sent.
    }
    if (!admin) return;
    try {
      if (s.tenant_selected) {
        const [a, r] = await Promise.all([
          nurseryAccountsService.getXeroAccounts(),
          nurseryAccountsService.getXeroTaxRates(),
        ]);
        if (a.success) setAccounts(a.data || []);
        if (r.success) setTaxRates(r.data || []);
        if (!a.success || !r.success) setListsError(errorMessage(a.success ? r : a, 'Could not read the accounts from Xero'));
      }
    } catch (e) {
      setListsError(errorMessage(e, 'Could not read the lists from Xero'));
    }
  }, []);

  /** Read the connection again, and invoicing's side of it where there is one. */
  const refresh = useCallback(async (c: XeroConnectionStatus) => {
    setConn(c);
    if (!sells) {
      setStatus(NO_INVOICING);
      return;
    }
    const res = await nurseryAccountsService.getXeroStatus();
    if (res.success && res.data) {
      setStatus(res.data);
      await loadRest(res.data, isAdmin);
    } else setError(errorMessage(res, 'Could not load the invoice settings'));
  }, [isAdmin, sells, loadRest]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await xeroConnectionService.getStatus();
      if (res.success && res.data) await refresh(res.data);
      else setError(errorMessage(res, 'Could not load the Xero settings'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the Xero settings'));
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (canRead && !adminLoading && !modulesLoading) load();
  }, [canRead, adminLoading, modulesLoading, load]);

  // Xero sends the browser back with a code; the person signed in here
  // completes the sign-in (see useOAuthReturn).
  const completeSignIn = useCallback((code: string, state: string) => xeroConnectionService.completeSignIn(code, state), []);
  const onSignedIn = useCallback(() => {
    setReturned({ connected: true, error: null });
    void load();
  }, [load]);
  const onSignInFailed = useCallback((message: string) => setReturned({ connected: false, error: null, message }), []);
  const finishingSignIn = useOAuthReturn(completeSignIn, onSignedIn, onSignInFailed, 'Xero was not connected. Please connect again from this page.');

  /** Run one action: one busy flag, one place errors land. */
  const run = async (key: string, fallback: string, action: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (e) {
      setError(errorMessage(e, fallback));
    } finally {
      setBusy(null);
    }
  };

  const connect = () => run('connect', 'Could not start the Xero sign-in', async () => {
    const res = await xeroConnectionService.getAuthorizeUrl();
    if (res.success && res.data?.authorization_url) window.location.href = res.data.authorization_url;
    else setError(errorMessage(res, 'Could not start the Xero sign-in'));
  });

  const disconnect = () => run('disconnect', 'Could not disconnect Xero', async () => {
    // Closed either way: an error shows on the page, not behind the dialog.
    setConfirmDisconnect(false);
    const res = await xeroConnectionService.disconnect();
    if (res.success && res.data) {
      await refresh(res.data);
      setNotice('Xero is disconnected. Nothing new is sent to it, and nothing is read back.');
    } else setError(errorMessage(res, 'Could not disconnect Xero'));
  });

  const saveMapping = () => run('mapping', 'Could not save the accounts', async () => {
    const res = await nurseryAccountsService.saveXeroMapping(mapping);
    if (res.success && res.data) {
      setStatus(res.data);
      setNotice(res.data.mapping_missing.length
        ? 'Saved. Nothing is sent to Xero until the accounts still missing are chosen.'
        : 'Saved. Invoices and credit notes now go to these accounts.');
    } else setError(errorMessage(res, 'Could not save the accounts'));
  });

  const sync = () => run('sync', 'Could not check Xero', async () => {
    const res = await nurseryAccountsService.syncXero();
    if (res.success && res.data?.status) {
      setStatus(res.data.status);
      await loadRest(res.data.status, isAdmin);
      setRetried({});
      // Say what actually happened: "sent" over a queue that did not move
      // reads as done when nothing went.
      const p = res.data.pushed;
      const sent = (p?.invoices ?? 0) + (p?.credit_notes ?? 0);
      const failed = p?.failed ?? 0;
      const left = (Number(res.data.status.queue?.invoices_pending) || 0) + (Number(res.data.status.queue?.credit_notes_pending) || 0);
      const parts = [
        sent ? `Sent ${p?.invoices ?? 0} invoice${p?.invoices === 1 ? '' : 's'} and ${p?.credit_notes ?? 0} credit note${p?.credit_notes === 1 ? '' : 's'} to Xero.` : 'Nothing was sent to Xero.',
        failed ? `${failed} ${failed === 1 ? 'was' : 'were'} refused; the reasons are listed below.` : '',
        left ? `${left} still waiting${sent ? '; press Check Xero now again to send more' : ''}.` : '',
        'Payments have been read back.',
      ];
      setNotice(parts.filter(Boolean).join(' '));
    } else setError(errorMessage(res, 'Could not check Xero'));
  });

  const retry = (kind: 'invoice' | 'credit_note', id: number) => run(`retry-${kind}-${id}`, 'Could not queue it again', async () => {
    const res = await nurseryAccountsService.retryXero(kind === 'invoice' ? { invoice_id: id } : { credit_note_id: id });
    if (res.success) setRetried((r) => ({ ...r, [`${kind}-${id}`]: true }));
    else setError(errorMessage(res, 'Could not queue it again'));
  });

  const backfill = () => run('backfill', 'Could not send the older invoices', async () => {
    setBackfillResult(null);
    const res = await nurseryAccountsService.backfillXero(backfillFrom);
    if (res.success && res.data) {
      const n = res.data.queued;
      setBackfillResult(n === 0
        ? `No unpaid invoices from ${formatLocalDateInTz(backfillFrom, tz)} were waiting to be sent.`
        : `${n} invoice${n === 1 ? ' is' : 's are'} now waiting to go to Xero.`);
      const s = await nurseryAccountsService.getXeroStatus();
      if (s.success && s.data) setStatus(s.data);
    } else setError(errorMessage(res, 'Could not send the older invoices'));
  });

  if (licenseLoading || adminLoading || modulesLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canRead) return <SeatRequired seat="user" what="The Xero page" />;

  const adminOnly = <p className="text-sm text-neutral-500 break-words">An Accounts Admin can change this.</p>;
  // Invoices can go: the connection holds invoicing's permissions and an
  // organisation is chosen.
  const ready = !!status?.connected && !!status?.tenant_selected;
  const connected = !!conn?.connected;
  const payrollReady = connected && !!conn?.tenant_selected && !!conn?.payroll;
  // A connection made before payroll and invoicing shared one holds only the
  // half it was made for. Only a half this organisation uses is worth naming.
  const lacks = [
    connected && sells && !conn?.invoicing ? 'invoices' : null,
    connected && paysStaff && !conn?.payroll ? 'payroll' : null,
  ].filter((x): x is string => !!x);
  // Signed in before the sign-in itself settled which Xero organisation, and
  // never settled since. There is nothing to choose from here: signing in
  // again settles it.
  const unsettled = connected && !conn?.tenant_selected;
  const nameOf = (code: string | null) => {
    if (!code) return null;
    const a = accounts.find((x) => x.code === code);
    return a ? `${a.code} — ${a.name}` : code;
  };
  const failureCount = failures.invoices.length + failures.credit_notes.length;

  return (
    <>
      <PageHeader
        title="Xero"
        subtitle="One connection to your Xero, for invoices and for payroll."
        icon={<BookOpen className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {returned.connected && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">
            Xero is connected.{' '}
            {sells
              ? 'Next, choose the accounts your invoices and credit notes go to.'
              : 'Next, match your people to their Xero employees.'}
          </div>
        )}
        {(returned.error || returned.message) && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">
            {returned.message ?? RETURN_ERRORS[returned.error ?? ''] ?? 'Xero was not connected. Please connect again from this page.'}
          </div>
        )}
        {finishingSignIn && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-sm text-neutral-700 break-words">
            Finishing the Xero sign-in…
          </div>
        )}
        {notice && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 break-words">{notice}</div>
        )}
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
        )}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : conn && status && (
          <>
            {!conn.enabled_here && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
                Xero only runs on the live system. On this test system nothing is sent to Xero and nothing is read from
                it, so you can try everything else safely.
              </div>
            )}

            <Step title="Step 1 — Connect">
              {connected ? (
                <>
                  <p className="text-sm text-neutral-800 break-words">
                    <span className="font-semibold">{orgName}</span> is connected to{' '}
                    <span className="font-semibold">{conn.tenant_name ?? 'Xero'}</span>
                    {conn.xero_user_name ? ` by ${conn.xero_user_name}` : ''}
                    {conn.connected_at ? ` on ${formatDateTimeInTz(conn.connected_at, tz)}` : ''}.
                  </p>
                  {unsettled && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
                      Xero was signed in to, but no Xero organisation was connected, so nothing is sent yet. Reconnect
                      and select {orgName}&rsquo;s own Xero organisation, and only that one, on Xero&rsquo;s screen.
                    </div>
                  )}
                  {!unsettled && lacks.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
                      This connection was made before invoices and payroll shared one, and does not yet cover{' '}
                      {joinWithAnd(lacks)}. Reconnect to add {lacks.length === 1 ? 'it' : 'them'}. Xero will show
                      your organisation already selected and greyed out: press Continue there. Nothing already set
                      up is lost.
                    </div>
                  )}
                  {isAdmin ? (
                    <div className="flex flex-wrap gap-2">
                      {(unsettled || lacks.length > 0) && (
                        <Button size="sm" onClick={connect} disabled={busy === 'connect'}>
                          {busy === 'connect' ? 'Opening Xero…' : 'Reconnect Xero'}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(true)}>Disconnect</Button>
                    </div>
                  ) : adminOnly}
                </>
              ) : (
                <>
                  <p className="text-sm text-neutral-700 break-words">
                    This connects Xero to <span className="font-semibold">{orgName}</span>, the organisation you are
                    in now, and to no other. It is connected once, and used for everything below:
                  </p>
                  <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-1 break-words">
                    {sells && <li>Invoices and credit notes go to Xero when a load is despatched, and payments come back.</li>}
                    {paysStaff && <li>Approved timesheets and leave are sent to Xero Payroll, and leave balances can be read from it.</li>}
                    {!sells && !paysStaff && <li>Invoices for a nursery, and timesheets and leave for payroll.</li>}
                    <li>Purchase orders can go to Xero, so each supplier&rsquo;s bill is made from what was ordered.</li>
                  </ul>
                  <p className="text-sm text-neutral-700 break-words">
                    On Xero&rsquo;s screen, select {orgName}&rsquo;s own Xero organisation and only that one. Whichever
                    one you select there is the one connected: there is no choosing between them here afterwards, and
                    a sign-in that selects more than one connects nothing.
                  </p>
                  {paysStaff && (
                    <p className="text-sm text-neutral-500 break-words">
                      Sign in to Xero as somebody with payroll admin rights, or payroll cannot be read.
                    </p>
                  )}
                  {isAdmin ? (
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={connect} disabled={busy === 'connect'}>
                        {busy === 'connect' ? 'Opening Xero…' : 'Connect to Xero'}
                      </Button>
                    </div>
                  ) : <p className="text-sm text-neutral-500 break-words">An Accounts Admin can connect Xero.</p>}
                </>
              )}
            </Step>

            {sells && (
              <Section
                title="Invoices"
                note="Invoices and credit notes go to your Xero when a load is despatched. Payments are matched to the bank in Xero and come back here."
              >
            {!ready && (
              <p className="text-sm text-neutral-500 break-words">
                {!connected
                  ? 'Connect Xero above to send invoices to it.'
                  : !conn.invoicing
                    ? 'Reconnect Xero above to send invoices to it.'
                    : 'Reconnect Xero above to send invoices to it.'}
              </p>
            )}

            {ready && (
              <Step title="Accounts">
                {status.mapping_missing.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900 break-words">
                    Nothing is sent to Xero until you choose {joinWithAnd(status.mapping_missing)}.
                  </div>
                )}
                {isAdmin ? (
                  <>
                    {listsError && <p className="text-sm text-red-700 break-words">{listsError}</p>}
                    {ACCOUNT_FIELDS.map((f) => {
                      const value = mapping[f.key] ?? '';
                      const known = accounts.some((a) => a.code === value);
                      return (
                        <div key={f.key} className="min-w-0">
                          <label htmlFor={`xero-${f.key}`} className={LABEL}>
                            {f.label}{f.required ? '' : ' (optional)'}
                          </label>
                          <select id={`xero-${f.key}`} value={value} className={INPUT}
                            onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}>
                            <option value="">{f.required ? 'Choose an account…' : 'Use the sales account'}</option>
                            {/* A saved code Xero no longer lists still has to show as what is saved. */}
                            {value && !known && <option value={value}>{value}</option>}
                            {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                          </select>
                          <p className="mt-1 text-xs text-neutral-500 break-words">{f.help}</p>
                        </div>
                      );
                    })}
                    <div className="min-w-0">
                      <label htmlFor="xero-gst" className={LABEL}>GST tax rate</label>
                      <select id="xero-gst" value={mapping.gst_tax_type ?? ''} className={INPUT}
                        onChange={(e) => setMapping((m) => ({ ...m, gst_tax_type: e.target.value || null }))}>
                        <option value="">Choose a tax rate…</option>
                        {mapping.gst_tax_type && !taxRates.some((t) => t.tax_type === mapping.gst_tax_type) && (
                          <option value={mapping.gst_tax_type}>{mapping.gst_tax_type}</option>
                        )}
                        {taxRates.map((t) => (
                          <option key={t.tax_type} value={t.tax_type}>{t.tax_type} — {t.name} ({Number(t.rate)}%)</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-neutral-500 break-words">The rate used on every line that carries GST.</p>
                    </div>
                    {PAYMENT_FIELDS.map((f) => {
                      const value = mapping[f.key] ?? '';
                      // An older server does not say which accounts take payments: offer them all.
                      const payable = accounts.filter((a) => a.takes_payments !== false);
                      const known = payable.some((a) => a.code === value);
                      return (
                        <div key={f.key} className="min-w-0">
                          <label htmlFor={`xero-${f.key}`} className={LABEL}>{f.label} (optional)</label>
                          <select id={`xero-${f.key}`} value={value} className={INPUT}
                            onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}>
                            <option value="">Not sent to Xero</option>
                            {value && !known && <option value={value}>{value}</option>}
                            {payable.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                          </select>
                          <p className="mt-1 text-xs text-neutral-500 break-words">{f.help}</p>
                        </div>
                      );
                    })}
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={saveMapping} disabled={busy === 'mapping'}>
                        {busy === 'mapping' ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <dl className="space-y-2">
                      {ACCOUNT_FIELDS.map((f) => (
                        <div key={f.key} className="min-w-0">
                          <dt className="text-xs text-neutral-500">{f.label}</dt>
                          <dd className="text-sm text-neutral-800 break-words">
                            {nameOf(status.mapping[f.key] ?? null) ?? (f.required ? 'Not chosen yet' : 'Uses the sales account')}
                          </dd>
                        </div>
                      ))}
                      <div className="min-w-0">
                        <dt className="text-xs text-neutral-500">GST tax rate</dt>
                        <dd className="text-sm text-neutral-800 break-words">{status.mapping.gst_tax_type ?? 'Not chosen yet'}</dd>
                      </div>
                      {PAYMENT_FIELDS.map((f) => (
                        <div key={f.key} className="min-w-0">
                          <dt className="text-xs text-neutral-500">{f.label}</dt>
                          <dd className="text-sm text-neutral-800 break-words">{nameOf(status.mapping[f.key] ?? null) ?? 'Not sent to Xero'}</dd>
                        </div>
                      ))}
                    </dl>
                    {adminOnly}
                  </>
                )}
              </Step>
            )}

            {ready && (
              <Step title="What is happening">
                <dl className="grid grid-cols-2 gap-3">
                  {([
                    ['Invoices waiting to go', status.queue.invoices_pending, false],
                    ['Invoices not sent', status.queue.invoices_failed, true],
                    ['Credit notes waiting to go', status.queue.credit_notes_pending, false],
                    ['Credit notes not sent', status.queue.credit_notes_failed, true],
                    ...(status.queue.counter_payments_failed
                      ? [['Counter payments not sent', status.queue.counter_payments_failed, true]] : []),
                  ] as [string, number, boolean][]).map(([label, count, bad]) => (
                    <div key={label} className="min-w-0 border border-neutral-200 rounded-lg p-3">
                      <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                      <dd className={`text-xl font-semibold ${bad && Number(count) > 0 ? 'text-red-700' : 'text-neutral-900'}`}>
                        {Number(count) || 0}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="text-sm text-neutral-700 break-words">
                  {status.last_sync_at
                    ? `Xero was last read on ${formatDateTimeInTz(status.last_sync_at, tz)}.`
                    : 'Xero has not been read yet.'}
                </p>
                {status.last_sync_error && (
                  <p className="text-sm text-red-700 break-words">The last check did not finish: {status.last_sync_error}</p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={sync} disabled={busy === 'sync'}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${busy === 'sync' ? 'animate-spin' : ''}`} />
                    {busy === 'sync' ? 'Checking Xero…' : 'Check Xero now'}
                  </Button>
                </div>
                {busy === 'sync' && (
                  <p className="text-sm text-neutral-500 break-words" role="status">
                    Sending what is waiting, then reading payments back. This can take a minute or two; keep this page open.
                  </p>
                )}

                {failureCount > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-neutral-900">Not sent to Xero</h3>
                    {!isAdmin && <p className="text-sm text-neutral-500 break-words">An Accounts Admin can send these again.</p>}
                    <ul className="space-y-2">
                      {failures.invoices.map((f) => {
                        const key = `invoice-${f.id}`;
                        return (
                          <li key={key} className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-1">
                            <p className="text-sm text-neutral-900 break-words">
                              Invoice{' '}
                              <Link to={`/accounts/invoices/${f.id}`} className="font-semibold text-primary-700 hover:underline">
                                {f.invoice_number}
                              </Link>{' '}
                              · {money(f.total_amount)}{f.customer_name ? ` · ${f.customer_name}` : ''}
                            </p>
                            <p className="text-sm text-red-700 break-words">{f.xero_sync_error || 'Xero gave no reason.'}</p>
                            {retried[key]
                              ? <p className="text-sm text-neutral-600">Waiting to go again.</p>
                              : isAdmin && (
                                <Button variant="outline" size="sm" onClick={() => retry('invoice', f.id)} disabled={busy === `retry-${key}`}>
                                  {busy === `retry-${key}` ? 'Queuing…' : 'Try again'}
                                </Button>
                              )}
                          </li>
                        );
                      })}
                      {failures.credit_notes.map((f) => {
                        const key = `credit_note-${f.id}`;
                        return (
                          <li key={key} className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-1">
                            <p className="text-sm text-neutral-900 break-words">
                              Credit note <span className="font-semibold">{f.credit_note_number}</span>{' '}
                              · {money(f.total_amount)}{f.customer_name ? ` · ${f.customer_name}` : ''}
                            </p>
                            <p className="text-sm text-red-700 break-words">{f.xero_sync_error || 'Xero gave no reason.'}</p>
                            {retried[key]
                              ? <p className="text-sm text-neutral-600">Waiting to go again.</p>
                              : isAdmin && (
                                <Button variant="outline" size="sm" onClick={() => retry('credit_note', f.id)} disabled={busy === `retry-${key}`}>
                                  {busy === `retry-${key}` ? 'Queuing…' : 'Try again'}
                                </Button>
                              )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </Step>
            )}

            {ready && isAdmin && (
              <Step title="Invoices from before you connected">
                <p className="text-sm text-neutral-700 break-words">
                  Invoices raised before Xero was connected are not sent by themselves. Choose a date and every
                  authorised, unpaid invoice from that date is sent. Paid, draft and cancelled invoices are left alone.
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-full sm:w-auto">
                    <label htmlFor="xero-backfill-from" className={LABEL}>Send invoices dated from</label>
                    <input id="xero-backfill-from" type="date" value={backfillFrom} className={INPUT}
                      onChange={(e) => { setBackfillFrom(e.target.value); setBackfillResult(null); }} />
                  </div>
                  <Button variant="outline" onClick={backfill} disabled={!backfillFrom || busy === 'backfill'}>
                    {busy === 'backfill' ? 'Sending…' : 'Send them'}
                  </Button>
                </div>
                {backfillResult && <p className="text-sm text-green-800 break-words" role="status">{backfillResult}</p>}
              </Step>
            )}

            <section className="text-sm text-neutral-600 space-y-1 break-words">
              <h3 className="font-semibold text-neutral-800">What Xero is used for</h3>
              <p>
                Xero is the ledger. Payments are received and matched to the bank in Xero, and read back here; a
                payment is never written to Xero from this system.
              </p>
              <p>
                An account that takes its invoices through its own portal is never emailed them, from here or from
                Xero. Copies stay on each invoice’s page.
              </p>
            </section>
              </Section>
            )}

            {sells && (
              <Section
                title="Bunnings remittances"
                note="Bunnings' rebate claims and remittances, recorded on the Remittances page, posted to Xero so the deposit matches one line."
              >
                <RemittanceXeroSettings
                  connected={connected}
                  ready={connected && !!conn.invoicing && !!conn.tenant_selected}
                  isAdmin={isAdmin}
                />
              </Section>
            )}

            <Section
              title="Purchase orders"
              note="Purchase orders go to Xero so each supplier's bill can be matched to what was ordered. Xero marks the order billed, and that shows on the order here."
            >
              <PurchaseOrderXeroSettings
                connected={connected}
                ready={connected && !!conn.invoicing && !!conn.tenant_selected}
                isAdmin={isAdmin}
                onReconnect={connect}
                reconnecting={busy === 'connect'}
              />
            </Section>

            {paysStaff && (
              <Section
                title="Payroll"
                note="Approved timesheets and leave are sent to Xero Payroll from the Payroll page. Which earnings rate, which employee and which leave type each goes to is set here."
              >
                {connected && !conn.payroll && (
                  <p className="text-sm text-neutral-500 break-words">Reconnect Xero above to use it for payroll.</p>
                )}
                {!isAdmin && payrollReady && adminOnly}
                <XeroPayrollPanel
                  ready={payrollReady}
                  tenantId={conn.tenant_id}
                  canPost={conn.can_post}
                  canEdit={isAdmin}
                />
              </Section>
            )}
          </>
        )}
      </div>

      {confirmDisconnect && (
        <Dialog
          title="Disconnect Xero"
          onClose={() => { if (busy !== 'disconnect') setConfirmDisconnect(false); }}
          footer={
            <>
              <Button variant="outline" onClick={() => setConfirmDisconnect(false)} disabled={busy === 'disconnect'}>Cancel</Button>
              <Button variant="danger" onClick={disconnect} disabled={busy === 'disconnect'}>
                {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            </>
          }
        >
          <ul className="list-disc pl-5 text-sm text-neutral-700 space-y-1 break-words">
            <li>Everything already sent stays in Xero.</li>
            {sells && <li>Nothing new is sent: invoices and credit notes stay here only.</li>}
            {sells && <li>Payments stop coming back, so they are entered here by hand until Xero is connected again.</li>}
            <li>Purchase orders stop going to Xero; the ones already there stay.</li>
            {paysStaff && <li>Timesheets and leave can no longer be sent to Xero Payroll.</li>}
            {paysStaff && <li>Leave balances go back to being worked out here, not read from Xero.</li>}
            <li>The account, earnings-rate, leave-type and employee choices are kept for when you connect again.</li>
          </ul>
        </Dialog>
      )}
    </>
  );
}

export default XeroSettings;
